import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { SERVICES } from "../data/content";
import { serviceSlug } from "../data/serviceDetails";

const MotionLink = motion(Link);

const ServiceCard = ({ service, index }) => {
  const Icon = service.icon;
  return (
    <MotionLink
      to={`/services/${serviceSlug(service.title)}`}
      data-testid={`service-card-${serviceSlug(service.title)}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      className="group relative flex flex-col rounded-2xl border hairline bg-card/60 p-6 transition-[border-color,box-shadow,background-color] duration-200 hover:border-primary/40 hover:shadow-[0_8px_40px_var(--green-glow)] sm:p-7"
    >
      <div className="flex items-start justify-between">
        <div className="rounded-xl border hairline bg-background/70 p-3 text-primary transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6">
          <Icon className="h-5 w-5" />
        </div>
        <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 transition-all duration-200 group-hover:translate-x-0.5 group-hover:opacity-100 group-hover:text-primary" />
      </div>
      <h3 className="mt-5 font-display text-lg font-bold tracking-tight">{service.title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{service.desc}</p>
      <span className="pointer-events-none absolute inset-x-6 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
    </MotionLink>
  );
};

export const Services = () => (
  <section id="services" className="relative py-24 sm:py-32" data-testid="services-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <SectionHeading
        index="03"
        eyebrow="Services"
        title="Technology services built around your business."
        sub="From a new product idea to modernizing an existing platform, Farmint provides end-to-end software engineering capabilities."
      />
      <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => (
          <ServiceCard key={s.title} service={s} index={i} />
        ))}
        <Reveal delay={0.2} className="h-full">
          <a
            href="#contact"
            data-testid="service-card-custom"
            className="group flex h-full min-h-[190px] flex-col justify-between rounded-2xl border border-dashed border-primary/30 bg-primary/[0.04] p-6 transition-colors duration-200 hover:border-primary/60 sm:p-7"
          >
            <p className="font-display text-lg font-bold tracking-tight">
              Something <span className="text-primary">specific</span> in mind?
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Tell us the problem — we&apos;ll shape the engineering approach around it.
            </p>
            <span className="mt-5 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.18em] text-primary">
              Talk to us <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);
